import React from 'react';
const { useState, useEffect } = React;
import { inputSt, labelSt, cardSt, mutedSt, badgeSt, fmtDate } from '../diagnostic/shared.jsx';
import { DAY_TEXT_LIMITS as DL } from '../../ownerSpaceConfig.js';
import { ownerCall, Notice } from './shared.jsx';

// Mi día · correo: the latest messages of ONE Gmail label Ale picks (Destacados
// by default), headers only (sender, subject, date), read live and never stored.
// It uses the Gmail connected in Configuración (no connection of its own: to
// connect, reconnect or check it, Configuración › Integraciones). "Convertir en
// tarea" creates one task per message with a link to it. Oike only reads here:
// nothing is marked as read, archived, deleted or sent. No AI.
// Only messages whose sender (whole address) is a contact or participant
// registered in Oike are listed; the server walks the label page by page and
// hands back a cursor for the older ones ("Buscar en correos anteriores").
// A failure is shown as an error with "Reintentar", never as an empty label.

import { errorText, labelSaveError, scanText } from '../../ownerMailErrors.js';
const smallBtn = { fontSize: 11, padding: '2px 8px' };
const openSettings = () => window.dispatchEvent(new CustomEvent('oike:openSettings', { detail: { tab: 'integrations' } }));

function ToTask({ m, date, onDone, onCancel }) {
  const [v, setV] = useState({ title: m.subject.slice(0, DL.taskTitle), withDate: true, priority: 'normal' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setBusy(true); setError('');
    try { await ownerCall({ action: 'mailToTask', messageId: m.id, task: { title: v.title, dueOn: v.withDate ? date : null, priority: v.priority } }); onDone(); }
    catch (e) { setError(e.message); setBusy(false); }
  };
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', margin: '6px 0 2px' }}>
      <input style={{ ...inputSt, flex: '2 1 220px', width: 'auto' }} value={v.title} maxLength={DL.taskTitle} onChange={e => setV({ ...v, title: e.target.value })} aria-label="Título de la tarea" />
      <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}><input type="checkbox" checked={v.withDate} onChange={e => setV({ ...v, withDate: e.target.checked })} /> para este día</label>
      <button className="action-btn btn-primary" disabled={busy || !v.title.trim()} onClick={save}>{busy ? 'Creando…' : 'Crear tarea'}</button>
      <button className="action-btn" onClick={onCancel}>Cancelar</button>
      {error && <span role="alert" style={{ color: '#9A6212', fontSize: 12 }}>{error}</span>}
    </div>
  );
}

export default function MailCard({ date, onTaskCreated }) {
  const [mail, setMail] = useState(null);
  const [list, setList] = useState(null);
  const [scan, setScan] = useState(null);
  const [listError, setListError] = useState('');
  const [labels, setLabels] = useState(null);
  const [open, setOpen] = useState(false);
  const [converting, setConverting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const loadStatus = () => ownerCall({ action: 'mailStatus' }).then(d => setMail(d.mail)).catch(e => setError(e.message));
  // more: go on with the older messages from the cursor (the list grows; nothing already shown is lost)
  const loadList = async (more = false) => {
    setBusy(true); setError(''); setListError('');
    try {
      const d = await ownerCall({ action: 'mailMessages', ...(more && scan?.cursor ? { cursor: scan.cursor } : {}) });
      setMail(m => ({ ...m, ...d.connection }));
      if (d.error) setListError(errorText(d.error));
      else if (d.connection?.status === 'error') setListError(errorText(d.connection.lastError));
      else {
        setList(prev => {
          if (!more || !prev) return d.messages;
          const seen = new Set(prev.map(m => m.id));
          return [...prev, ...d.messages.filter(m => !seen.has(m.id))];
        });
        setScan(prev => (more && prev ? { ...d.scan, scanned: prev.scanned + d.scan.scanned, hidden: prev.hidden + d.scan.hidden } : d.scan));
      }
    } catch (e) { setListError(e.message || 'No se pudieron leer los correos.'); }
    setBusy(false);
  };
  useEffect(() => { loadStatus(); }, []);
  useEffect(() => { if (open && mail?.connected && list === null) loadList(); }, [open, mail?.connected]);
  // Saved means Airtable kept it (the server reads it back): only then the label changes and its messages load.
  // A failure keeps the previous label shown and selected, and says why
  const pickLabel = async (labelId) => {
    if (labelId === mail.settings?.labelId) return;
    const previous = mail.settings?.labelName;
    setBusy(true); setError('');
    try {
      const r = await ownerCall({ action: 'mailSetLabel', labelId });
      setMail(m => ({ ...m, settings: r.settings }));
      setLabels(null); setList(null); setScan(null);
      setBusy(false);
      await loadList();
      return;
    } catch (e) { setError(labelSaveError(e, previous)); }
    setBusy(false);
  };
  const showLabels = async () => { setError(''); try { const d = await ownerCall({ action: 'mailLabels' }); if (d.error) setError(errorText(d.error)); setLabels(d.labels); } catch (e) { setError(e.message); } };

  if (!mail) return null;
  return (
    <div style={cardSt}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={labelSt}>Correos para revisar</span>
        {!mail.available ? <span style={mutedSt}>{mail.reason === 'disabled_in_test' ? 'desactivado en el entorno de prueba' : 'Gmail no está configurado en este entorno.'}</span>
          : !mail.connected ? <><span style={mutedSt}>Gmail no está conectado.</span><button className="action-btn" style={smallBtn} onClick={openSettings}>Conectar en Configuración</button></>
            : (
              <>
                <span style={badgeSt(mail.status === 'connected')}>{mail.status === 'connected' ? 'Gmail de Configuración' : 'con error'}</span>
                {mail.accountEmail && <span style={mutedSt}>{mail.accountEmail}</span>}
                <span style={mutedSt}>· etiqueta <b>{mail.settings?.labelName}</b></span>
                {list && !listError && <span style={mutedSt}>· {list.length} de personas registradas{scan?.cursor ? ' (búsqueda incompleta: quedan correos más antiguos sin revisar)' : ''}</span>}
                <button className="action-btn" style={smallBtn} onClick={() => setOpen(o => !o)} aria-expanded={open}>{open ? 'Ocultar' : 'Ver correos'}</button>
              </>
            )}
      </div>
      {mail.available && !mail.connected && <div style={{ ...mutedSt, marginTop: 4 }}>Usa la misma conexión de Gmail que el seguimiento comercial. Marca con una estrella (o una etiqueta tuya) los correos que requieren una acción y aquí los conviertes en tareas.</div>}
      {mail.connected && mail.status === 'error' && !(open && listError) && <div style={{ color: '#9A6212', fontSize: 12, marginTop: 4 }}>{errorText(mail.lastError)} {['reconnect_needed', 'scope_insufficient', 'api_disabled'].includes(mail.lastError) && <button className="action-btn" style={smallBtn} onClick={openSettings}>Abrir Configuración</button>}</div>}
      {error && <div role="alert" style={{ color: '#9A6212', fontSize: 12, marginTop: 4 }}>{error}</div>}
      {open && mail.connected && (
        <div style={{ marginTop: 8 }}>
          <Notice icon="✉️" tone="private">Son correos para revisar, no tareas: pasan a ser tareas solo si los conviertes. Solo aparecen correos cuyo remitente (el email completo) es un contacto o participante registrado en Oike; los demás quedan fuera y no se crea ningún contacto. Solo se leen remitente, asunto y fecha de la etiqueta elegida, en el momento y sin guardarlos. Oike no abre el cuerpo ni adjuntos aquí, ni marca como leído, archiva, borra o envía. Al convertir uno en tarea, la tarea guarda el asunto, el remitente, la fecha y el enlace al correo. Nada va a la IA.</Notice>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
            <button className="action-btn" style={smallBtn} disabled={busy} onClick={() => loadList(false)}>{busy ? 'Leyendo…' : 'Actualizar'}</button>
            {!labels ? <button className="action-btn" style={smallBtn} onClick={showLabels}>Cambiar etiqueta</button> : (
              <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}>Etiqueta
                <select style={{ ...inputSt, width: 'auto', fontSize: 12 }} value={mail.settings?.labelId} disabled={busy} aria-label="Etiqueta de correo" onChange={e => pickLabel(e.target.value)}>
                  {labels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
                <button className="action-btn" style={smallBtn} onClick={() => setLabels(null)}>Listo</button>
              </label>
            )}
            {mail.checkedAt && <span style={mutedSt}>leído {fmtDate(mail.checkedAt, true)}</span>}
          </div>
          {listError ? (
            <div role="alert" style={{ color: '#9A6212', fontSize: 12, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>{listError}</span>
              <button className="action-btn" style={smallBtn} disabled={busy} onClick={() => loadList(false)}>{busy ? 'Leyendo…' : 'Reintentar'}</button>
              {['reconnect_needed', 'scope_insufficient', 'api_disabled'].includes(mail.lastError) && <button className="action-btn" style={smallBtn} onClick={openSettings}>Abrir Configuración</button>}
            </div>
          ) : list === null ? <div style={mutedSt}>Cargando…</div> : (
            <>
              {scan && <div style={{ ...mutedSt, marginBottom: 4 }}>{scanText(scan, mail.settings?.labelName, list.length)}</div>}
              {!list.length && <div style={mutedSt}>{scan?.cursor ? 'Todavía no hay correos de personas registradas entre los revisados.' : `No hay correos de personas registradas en «${mail.settings?.labelName}».`}</div>}
              {list.map(m => (
                <div key={m.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--globant-border)', fontSize: 13 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: m.unread ? 700 : 400, flex: '1 1 220px', minWidth: 0, overflowWrap: 'anywhere' }}>{m.subject}</span>
                    <span style={mutedSt} title={m.fromEmail || ''}>{m.from}{m.receivedAt ? ` · ${fmtDate(m.receivedAt)}` : ''}</span>
                    <a href={m.link} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--globant-green)', fontSize: 11 }}>abrir en Gmail</a>
                    {m.task ? <span style={badgeSt(true)}>ya es tarea{m.task.status === 'done' ? ' · hecha' : ''}</span>
                      : converting !== m.id && <button className="action-btn" style={smallBtn} onClick={() => setConverting(m.id)}>Convertir en tarea</button>}
                  </div>
                  {converting === m.id && <ToTask m={m} date={date} onCancel={() => setConverting(null)} onDone={() => { setConverting(null); loadList(false); onTaskCreated?.(); }} />}
                </div>
              ))}
              {scan?.cursor && <button className="action-btn" style={{ ...smallBtn, marginTop: 6 }} disabled={busy} onClick={() => loadList(true)}>{busy ? 'Buscando…' : 'Buscar en correos anteriores'}</button>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
